<template>
  <div class="relative space-y-6 lg:space-y-8 px-3 sm:px-4 lg:px-8 min-h-[calc(100vh-8rem)]">
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
      <div>
        <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary dark:text-secondary">
          Completed Forms
        </h1>
        <p class="text-sm text-slate-500 dark:text-slate-200 mt-2">
          View forms completed by employees.
        </p>
      </div>
      <div class="flex items-center gap-2">
        <button @click="showStats = !showStats"
          class="px-4 py-2 rounded-lg font-semibold transition border border-gray-300 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-gray-700 inline-flex items-center justify-center w-full sm:w-auto">
          <Eye v-if="!showStats" class="h-5 w-5 mr-2" />
          <EyeOff v-else class="h-5 w-5 mr-2" />
          <span class="hidden sm:inline">{{ showStats ? 'Hide Stats' : 'Show Stats' }}</span>
        </button>
      </div>
    </div>

    <!-- Stats -->
    <Transition name="stats">
      <div v-if="showStats" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
        <StatCard label="Total" :value="stats.total" :loading="loading" :icon="FileText" color="text-blue-600 dark:text-blue-400" />
        <StatCard label="New" :value="stats.new" :loading="loading" :icon="Mail" color="text-blue-600 dark:text-blue-400" />
        <StatCard label="Read" :value="stats.read" :loading="loading" :icon="Eye" color="text-emerald-600 dark:text-emerald-400" />
        <StatCard label="Deleted" :value="stats.deleted" :loading="loading" :icon="Trash2" color="text-rose-600 dark:text-rose-400" />
      </div>
    </Transition>

    <TableControls
      v-model:search="searchQuery"
      v-model:status="statusFilter"
      v-model:perPage="perPage"
      :options="statusOptions"
      filterLabel="Status"
      placeholder="Search by subject..."
      class="md:sticky md:top-[80px]"
    />

    <div v-if="error" class="bg-red-50 text-red-600 p-4 rounded-xl border border-red-200">
      {{ error }}
    </div>

    <div v-else class="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200/50 dark:border-slate-700/50 overflow-hidden">
      <!-- Desktop Table View -->
      <div class="hidden lg:block overflow-x-auto">
        <table class="min-w-full divide-y divide-slate-200/50 dark:divide-slate-700/50">
          <thead class="bg-slate-50 dark:bg-slate-800/50">
            <tr>
              <th class="px-6 py-4 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">Submitted By</th>
              <th class="px-6 py-4 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">Form Name</th>
              <th class="px-6 py-4 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">Date Submitted</th>
              <th class="px-6 py-4 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">Type</th>
              <th class="px-6 py-4 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">Action</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200/50 dark:divide-slate-700/50">
            <TableLoader v-if="loading" :colspan="5" title="Fetching Forms" />
            <tr v-else-if="paginatedForms.length === 0">
              <td colspan="5" class="px-6 py-12 text-center text-slate-500">No completed forms found.</td>
            </tr>
            <tr v-for="form in paginatedForms" :key="form.form_id" class="hover:bg-slate-50 dark:hover:bg-slate-800/50">
              <td class="px-6 py-4">
                <div class="flex items-center gap-3 justify-start">
                  <template v-if="shouldShowProfileImage(form)">
                    <img
                      :src="profileImageUrl(form)"
                      :alt="`${submittedByName(form)} photo`"
                      class="h-10 w-10 rounded-full object-cover shadow-md flex-shrink-0"
                      @error="onProfileImageError(form)"
                    />
                  </template>
                  <div
                    v-else
                    class="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold shadow-md bg-gradient-to-br flex-shrink-0"
                    :class="submittedByBgClass(form)"
                  >
                    {{ submittedByInitials(form) }}
                  </div>
                  <div class="min-w-0 text-left">
                    <div class="text-sm font-semibold text-slate-700 dark:text-slate-300">
                      {{ form.form_from || '—' }}
                    </div>
                    <div class="text-sm text-slate-500 dark:text-slate-400">
                      {{ form.user?.email || '' }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 text-sm font-medium text-slate-700 dark:text-slate-300">{{ form.form_subject || '—' }}</td>
              <td class="px-6 py-4 text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">{{ formatDate(form.date_sent) }}</td>
              <td class="px-6 py-4">
                <span class="px-2.5 py-1 text-xs font-semibold rounded-full" :class="typeClass(form.type)">{{ form.type || '—' }}</span>
              </td>
              <td class="px-6 py-4">
                <div class="flex gap-2 justify-center">
                  <button @click="viewForm(form)" class="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors" title="View">
                    <Eye class="w-5 h-5 text-blue-600" />
                  </button>
                  <button @click="printForm(form)" class="p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors" title="Print">
                    <Printer class="w-5 h-5" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile Card View -->
      <div class="lg:hidden divide-y divide-slate-200/50 dark:divide-slate-700/50">
        <div v-if="loading" class="p-8 flex justify-center">
          <Loader2 class="w-8 h-8 animate-spin text-primary" />
        </div>
        <div v-else-if="paginatedForms.length === 0" class="px-6 py-12 text-center text-slate-500">No completed forms found.</div>
        <div v-else v-for="form in paginatedForms" :key="form.form_id" class="p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50">
          <div class="flex items-center gap-3 mb-2">
            <template v-if="shouldShowProfileImage(form)">
              <img :src="profileImageUrl(form)" :alt="`${submittedByName(form)} photo`" class="h-10 w-10 rounded-full object-cover shadow-md flex-shrink-0" @error="onProfileImageError(form)" />
            </template>
            <div v-else class="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold shadow-md bg-gradient-to-br flex-shrink-0" :class="submittedByBgClass(form)">
              {{ submittedByInitials(form) }}
            </div>
            <div class="min-w-0 flex-1">
              <div class="text-sm font-semibold text-slate-700 dark:text-slate-300">{{ form.form_from || '—' }}</div>
              <div class="text-xs text-slate-500 dark:text-slate-400 truncate">{{ form.user?.email || '' }}</div>
            </div>
          </div>
          <div class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{{ form.form_subject || '—' }}</div>
          <div class="flex items-center gap-2 mb-2">
            <span class="px-2 py-1 text-xs font-semibold rounded-full" :class="typeClass(form.type)">{{ form.type || '—' }}</span>
            <span class="text-xs text-slate-500 dark:text-slate-400">{{ formatDate(form.date_sent) }}</span>
          </div>
          <div class="flex gap-2">
            <button @click="viewForm(form)" class="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors" title="View">
              <Eye class="w-5 h-5 text-blue-600" />
            </button>
            <button @click="printForm(form)" class="p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors" title="Print">
              <Printer class="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="!loading && !error && filteredTotal > 0"
      class="card border border-gray-200/50 dark:border-gray-700/50 shadow-sm sticky bottom-0 z-30"
    >
      <div class="p-4">
        <PaginationBar
          :current-page="currentPage"
          :total-pages="filteredTotalPages"
          :total="filteredTotal"
          :per-page="perPage"
          @update:page="goToPage"
        />
      </div>
    </div>

    <!-- View Form Modal -->
    <transition name="fade">
      <div v-if="showViewModal" class="fixed inset-0 z-50 overflow-y-auto" @click.self="showViewModal = false">
        <div class="flex sm:items-center sm:justify-center min-h-screen sm:px-4 sm:pt-4 sm:pb-20 text-center">
          <div class="fixed inset-0 transition-opacity bg-black bg-opacity-50 dark:bg-opacity-50" @click="showViewModal = false"></div>
          <div class="inline-block align-bottom sm:align-middle bg-white dark:bg-gray-800 sm:rounded-lg text-left overflow-hidden shadow-xl transform transition-all min-h-screen w-full fixed inset-x-0 top-0 sm:static sm:min-h-0 sm:max-w-4xl sm:w-full flex flex-col">
            <div class="bg-gradient-to-tr from-primary to-secondary dark:from-primary-light/10 dark:to-secondary-light/10 px-4 py-4 sm:px-6 flex items-center justify-between flex-shrink-0">
              <h3 class="text-lg font-bold text-white truncate">{{ viewFormData?.form_subject || 'Form Content' }}</h3>
              <button @click="showViewModal = false" class="shrink-0 p-1.5 rounded-lg hover:bg-white/10 text-white/80 hover:text-white transition-colors">
                <X class="h-5 w-5" />
              </button>
            </div>
            <div class="bg-white dark:bg-gray-800 px-3 pt-4 pb-3 sm:p-6 sm:pb-4 overflow-y-auto flex-1 max-h-[calc(100vh-12rem)]">
              <template v-if="viewFiles.length > 0">
                <div v-for="(file, i) in viewFiles" :key="i" class="mb-5 last:mb-0">
                  <p class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2 truncate">{{ file.file_name || `File ${i + 1}` }}</p>
                  <div v-if="file.is_previewable" class="flex items-center justify-center">
                    <img v-if="['jpg','jpeg','png','gif','webp'].includes(file.file_extension)" :src="file.file_url" class="max-w-full max-h-[70vh] rounded-lg" />
                    <iframe v-else-if="file.file_extension === 'pdf'" :src="file.file_url" class="w-full h-[70vh] rounded-lg"></iframe>
                  </div>
                  <div v-else class="text-center py-8 text-slate-500 border border-dashed rounded-xl">
                    <FileText class="h-12 w-12 mx-auto mb-2 text-slate-300 dark:text-slate-600" />
                    <p class="text-base font-medium">Preview not available</p>
                    <p class="text-sm mt-1">(.{{ file.file_extension }})</p>
                  </div>
                </div>
              </template>
              <div v-else class="form-content" v-html="viewFormData?.form_content || 'No content available.'" />
            </div>
            <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 flex flex-col gap-2 sm:px-6 sm:flex-row-reverse sm:gap-0 shrink-0">
              <template v-for="(file, i) in viewFiles" :key="i">
                <button @click="handleDownload(file)" class="w-full sm:w-auto sm:ml-3 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-primary hover:bg-secondary shadow-sm transition-all inline-flex items-center justify-center gap-2">
                  <DownloadIcon class="h-4 w-4" /> Download File {{ viewFiles.length > 1 ? i + 1 : '' }}
                </button>
              </template>
              <button type="button" class="w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-secondary-text dark:text-secondary-dark-text hover:bg-gray-100/80 dark:hover:bg-gray-700/60 transition-colors" @click="showViewModal = false">Close</button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { Eye, EyeOff, FileText, Mail, Trash2, Printer, Download as DownloadIcon, X, Loader2 } from 'lucide-vue-next'
import { completedFormService, type CompletedForm } from '@/services/completedFormService'
import TableControls from '@/components/layout/TableControls.vue'
import PaginationBar from '@/components/layout/PaginationBar.vue'
import StatCard from '@/components/layout/StatCard.vue'
import TableLoader from '@/components/ui/TableLoader.vue'
import api from '@/services/api'
import { useToast } from 'vue-toastification'

const toast = useToast()
const toastTimeout = 5000

const avatarLoadFailed = ref<Set<number>>(new Set())

const forms = ref<CompletedForm[]>([])
const loading = ref(false)
const error = ref('')
const showStats = ref(false)

const searchQuery = ref('')
const statusFilter = ref('')

const statusOptions = [
  { label: 'All', value: '' },
  { label: 'New', value: 'new' },
  { label: 'Read', value: 'read' },
  { label: 'Deleted', value: 'deleted' },
]

const showViewModal = ref(false)
const viewFormData = ref<CompletedForm | null>(null)

const viewFiles = computed(() => {
  if (viewFormData.value?.files && viewFormData.value.files.length > 0) {
    return viewFormData.value.files
  }
  if (viewFormData.value?.file_url) {
    return [{
      file_url: viewFormData.value.file_url,
      file_extension: viewFormData.value.file_extension || '',
      is_previewable: viewFormData.value.is_previewable || false,
    }]
  }
  return []
})

const stats = computed(() => {
  const all = forms.value
  return {
    total: all.length,
    new: all.filter(f => f.status === 'new').length,
    read: all.filter(f => f.status === 'read').length,
    deleted: all.filter(f => f.status === 'deleted').length,
  }
})

const statusFiltered = computed(() => {
  const list = forms.value
  if (!statusFilter.value) return list
  return list.filter(f => f.status === statusFilter.value)
})

const searchFiltered = computed(() => {
  const list = statusFiltered.value
  if (!searchQuery.value.trim()) return list
  const q = searchQuery.value.toLowerCase()
  return list.filter(f =>
    (f.form_subject || '').toLowerCase().includes(q)
  )
})

const perPage = ref(10)
const currentPage = ref(1)
const filteredTotalPages = computed(() => Math.max(1, Math.ceil(searchFiltered.value.length / perPage.value)))
const filteredTotal = computed(() => searchFiltered.value.length)

const paginatedForms = computed(() => {
  const start = (currentPage.value - 1) * perPage.value
  const end = start + perPage.value
  return searchFiltered.value.slice(start, end)
})

watch([statusFilter, searchQuery, perPage], () => {
  currentPage.value = 1
})

const goToPage = (page: number) => {
  currentPage.value = page
}

const formatDate = (date?: string) => {
  if (!date) return '—'
  const d = new Date(date)
  const opts: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' }
  return d.toLocaleDateString('en-US', opts) + ' [' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) + ']'
}

const submittedByName = (form: CompletedForm) => {
  const f = form.user?.details?.first_name || ''
  const l = form.user?.details?.last_name || ''
  if (f && l) return `${f} ${l}`
  return form.form_from || form.user?.email || 'Unknown'
}

const submittedByInitials = (form: CompletedForm) => {
  const f = form.user?.details?.first_name || ''
  const l = form.user?.details?.last_name || ''
  if (f && l) return (f[0] + l[0]).toUpperCase()
  const name = form.form_from || ''
  const parts = name.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return name.substring(0, 2).toUpperCase() || '?'
}

const submittedByBgClass = (form: CompletedForm) => {
  const gradients = [
    'from-primary to-primary-hover dark:from-primary-dark dark:to-primary-dark-hover',
    'from-success to-success-hover dark:from-success-dark dark:to-success-dark-hover',
    'from-info to-info-hover dark:from-info-dark dark:to-info-dark-hover',
    'from-warning to-warning-hover dark:from-warning-dark dark:to-warning-dark-hover',
    'from-danger to-danger-hover dark:from-danger-dark dark:to-danger-dark-hover',
  ]
  return gradients[(form.user_id || 0) % gradients.length]
}

const profileImageUrl = (form: CompletedForm) => {
  const raw = String(form.user?.profile_picture || '').trim()
  if (!raw || raw.toLowerCase() === 'null') return ''
  if (/^https?:\/\//i.test(raw) || raw.startsWith('data:image/') || raw.startsWith('blob:')) return raw
  const rawBase = String(api.defaults.baseURL || '').replace(/\/$/, '')
  const baseNoApi = rawBase.replace(/\/api$/i, '')
  if (raw.startsWith('/storage/')) return `${baseNoApi}${raw}`
  if (raw.startsWith('storage/')) return `${baseNoApi}/${raw}`
  return `${baseNoApi}/storage/${raw.replace(/^\//, '')}`
}

const shouldShowProfileImage = (form: CompletedForm) => {
  const url = profileImageUrl(form)
  return Boolean(url) && !avatarLoadFailed.value.has(form.user_id || 0)
}

const onProfileImageError = (form: CompletedForm) => {
  avatarLoadFailed.value.add(form.user_id || 0)
}

const typeClass = (type?: string) => {
  switch (type) {
    case 'Doc File': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
    case 'Online form': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
    case 'URL Link': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
    default: return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
  }
}

const viewForm = (form: CompletedForm) => {
  viewFormData.value = form
  showViewModal.value = true
}

const handleDownload = async (file: { download_url?: string; file_name?: string }) => {
  const url = file.download_url
  if (!url) return
  try {
    const token = localStorage.getItem('token')
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) throw new Error('Download failed')
    const blob = await response.blob()
    const blobUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = file.file_name || 'download'
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(blobUrl)
    document.body.removeChild(a)
  } catch {
    // fallback: open in new tab
    if (file.download_url) {
      window.open(file.download_url, '_blank')
    }
  }
}

const printForm = async (form: CompletedForm | null) => {
  if (!form) return
  if (form.form_content) {
    const win = window.open('', '_blank')
    if (!win) return
    win.document.write('<html><head><title>' + (form.form_subject || 'Form') + '</title>')
    win.document.write('<style>body { padding: 20px; font-family: Arial, sans-serif; }</style>')
    win.document.write('</head><body>')
    win.document.write(form.form_content)
    win.document.write('</body></html>')
    win.document.close()
    win.print()
    toast.success('Document printed successfully', { timeout: toastTimeout })
  } else if (form.files?.length) {
    const loadingToast = toast.info('Preparing document for printing...', { timeout: false })
    let downloadUrl = form.files[0].download_url
    if (!downloadUrl) {
      downloadUrl = completedFormService.getDownloadUrl(form.form_id as any)
    }
    try {
      await completedFormService.printFile(downloadUrl)
      toast.dismiss(loadingToast)
      toast.success('Document printed successfully', { timeout: toastTimeout })
    } catch (err: any) {
      toast.dismiss(loadingToast)
      toast.error(err.message || 'Failed to print file', { timeout: toastTimeout })
    }
  }
}

const fetchForms = async () => {
  loading.value = true
  error.value = ''
  try {
    forms.value = await completedFormService.fetchAll()
  } catch (err: any) {
    error.value = 'Failed to load completed forms.'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchForms()
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
